// General

import { dateHandler } from "./date-utils"

export const getUniqueArrayItems = (array) => {
    function isElementInArray(needle, haystack) {
        for (var i = 0; i < haystack.length; i++) {
            if (needle === haystack[i]) {
                return true
            }
        }
        return false
    }

    var unique = []

    for (var i = 0; i < array.length; i++) {
        if (!isElementInArray(array[i], unique)) {
            unique.push(array[i])
        }
    }

    return unique
}

export const deepCopy = (obj) => JSON.parse(JSON.stringify(obj))

export function deepClone(obj) {
    if (obj === null || typeof obj !== 'object') {
        return obj;
    }

    if (obj instanceof Date) {
        return dateHandler.toZeroHoursFormat(obj);
    }

    if (obj instanceof RegExp) {
        return new RegExp(obj.source, obj.flags);
    }

    if (Array.isArray(obj)) {
        return obj.map(deepClone);
    }

    const clonedObj = {};
    for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
            clonedObj[key] = deepClone(obj[key]);
        }
    }
    return clonedObj;
}

export const scrollToTop = (el, modifier = 0) => {
    window.scrollTo({
        top: el
            ? el.offsetTop + modifier
            : 0 + modifier,
        behavior: 'smooth'
    })
}

export const boolToInt = (bool) => {
    return bool ? 1 : 0
}


// Fetch

export const fetchRetryWithDelay = async (req, retries = 3, delay = 1000) => {
    try {
        const result = await req()
        return result
    } catch (err) {
        if (retries === 0) {
            throw err
        }

        await new Promise((resolve) => setTimeout(resolve, delay))

        return fetchRetryWithDelay(req, retries - 1, delay)
    }
}



// Dates

export const isDate = (date) => {
    return date instanceof Date && !isNaN(date)
}

const DAYS = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Day',
    'Weekday',
    'Weekend Day',
]

const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Day', 'Wkday', 'Wkend']

const MONTHS = [
    'January',
    'Febuary',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
]

const MONTHS_SHORT = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sept',
    'Oct',
    'Nov',
    'Dec',
]

export const getDayName = (date, short) => {
    return short ? DAYS_SHORT[date.getDay()] : DAYS[date.getDay()]
}

export const getDayNameByIndex = (index, short) => {
    return short ? DAYS_SHORT[index] : DAYS[index]
}

export const getMonthName = (date, short) => {
    return short ? MONTHS_SHORT[date.getMonth()] : MONTHS[date.getMonth()]
}

export const getNumberOfDaysBetweenDates = (startDate, endDate) => {
    const start = dateHandler.toZeroHoursFormat(startDate)
    const end = dateHandler.toZeroHoursFormat(endDate)

    const days = Math.round((end - start) / (1000 * 60 * 60 * 24))

    return days
}

export const setZeroHours = (date) => {
    const d = new Date(date)
    d.setHours(0, 0, 0, 0)
    return d
}

/**
 * Accepts Date object or JS Date string
 * Returns YYYY-MM-DD string
 * @param {Date | string} date
 * @return {string} YYYY-MM-DD
**/
export const getSQLDateFormat = (date) => {
    let d = date instanceof Date ? date : new Date(date)

    if (!(d instanceof Date)) {
        return null
    }

    const formatted =
        d.getFullYear().toString().padStart(4, '0') +
        '-' +
        (d.getMonth() + 1).toString().padStart(2, '0') +
        '-' +
        d.getDate().toString().padStart(2, '0')
    return formatted
}

/**
 * Accepts .toISOString() format for date arg ('yyyy-MM-ddTHH:mm:ss.mmmZ'), int for adjustment
 * Returns YYYY-MM-DD string with adjustment applied
 * @param {string} date
 * @return {Date}
**/
export const getAdjustedDate = (date, adjustment) => {
    if (!adjustment) {
        return date
    }

    let dateSplit = date.split('-')
    let formattedDate = dateHandler.toZeroHoursFormat(date)

    formattedDate.setDate(
        (parseInt(dateSplit[2]) + parseInt(adjustment))
            .toString()
            .padStart(2, '0')
    )

    return formattedDate
}

export const nthNumber = (number) => {
    if (number > 3 && number < 21) return 'th'
    switch (number % 10) {
        case 1:
            return 'st'
        case 2:
            return 'nd'
        case 3:
            return 'rd'
        default:
            return 'th'
    }
}

export const getRecurrenceDisplayString = (val, sortable) => {
    const sortableOrdinals = { daily: '01', weekly: '02', monthly: '03', yearly: '04' };
    const { day_of_week, week_of_month, month_of_year, day_of_month_list, recurring_type, separation_count } = val;
    
    const sortablePrefix = sortable ? `${sortableOrdinals[recurring_type] || '00'} ` : '';
    let displayString = '';
    
    const separationSuffix = (unit) => separation_count > 1 ? `— every ${separation_count} ${unit}` : '';
    
    switch (recurring_type) {
        case 'daily':
            displayString = `Daily — every ${separation_count} days`;
            break;

        case 'weekly':
            displayString = `Weekly — ${getDayNameByIndex(day_of_week)} ${separationSuffix('weeks')}`;
            break;

        case 'monthly':
            if (week_of_month) {
                const weekText = week_of_month === 'last'
                    ? 'Last'
                    : `${week_of_month}${nthNumber(week_of_month)}`;
                displayString = `Monthly — ${weekText} ${getDayNameByIndex(parseInt(day_of_week))} ${separationSuffix('months')}`;
            } else if (day_of_month_list?.length) {
                displayString = `Monthly — ${sortable
                    ? day_of_month_list.toString().padStart(2, '0')
                    : getDayOfMonthListDisplayValue(day_of_month_list)} ${separationSuffix('months')}`;
            }
            break;

        case 'yearly':
            if (week_of_month) {
                const weekText = week_of_month === 'last'
                    ? 'Last'
                    : `${week_of_month}${nthNumber(week_of_month)}`;
                displayString = `Yearly — ${weekText} ${getDayNameByIndex(parseInt(day_of_week))} of ${getMonthName(new Date(0, parseInt(month_of_year)))} ${separationSuffix('years')}`;
            } else if (day_of_month_list?.length) {
                displayString = `Yearly — ${sortable
                    ? day_of_month_list.toString().padStart(2, '0')
                    : getDayOfMonthListDisplayValue(day_of_month_list)} of ${getMonthName(new Date(0, parseInt(month_of_year)))} ${separationSuffix('years')}`;
            }
            break;

        default:
            const dateValue = val?.instanceDate ?? val?.start_date ?? val?.startDate;
            if (!dateValue) return "";
            displayString = `Once — ${dateHandler.toDisplayFormat(dateHandler.toSubmitFormat(dateHandler.toZeroHoursFormat(dateValue)))}`;
    }
    
    return sortablePrefix + displayString;
};


const getDayOfMonthListDisplayValue = (dayOfMonthList) => {
    let dayOfMonthListDisplay;
    if (Array.isArray(dayOfMonthList)) {
        dayOfMonthListDisplay = dayOfMonthList
            .map((day) => day + nthNumber(day))
            .join(', ')
    } else {
        dayOfMonthListDisplay = dayOfMonthList.split(',')
            .map(Number)
            .sort((a, b) => a - b)
            .map((day) => day + nthNumber(day))
            .join(', ')
    }

    return dayOfMonthListDisplay
}

export const getDefaultEndDate = (startDate, additionalYears = 3) => {
    const endYear = startDate.getFullYear() + additionalYears
    const defaultEndDate = new Date(`${endYear}-12-31T23:59:59Z`)
    return defaultEndDate
}



// Currency

export const formatCurrency = (amount) => {
    const currencyFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    })
    return currencyFormatter.format(amount)
}

export const getAnnualTransactionAmount = (transaction) => {
    const recurringType = transaction.recurring_type
    const separationCount = transaction.separation_count || 1
    const amount = parseFloat(transaction.amount)

    if (recurringType === 'daily') return (amount * 365) / separationCount
    if (recurringType === 'weekly') return (amount * 52) / separationCount
    if (recurringType === 'monthly') return (amount * 12) / separationCount
    if (recurringType === 'yearly') return amount / separationCount

    return amount
}



// Reporting

export const getMonthlyMinMax = (transactionsList) => {
    const transactions = deepClone(transactionsList)
    const transactionDatesAndBalances = transactions.map((transaction) => {
        const date = dateHandler.toZeroHoursFormat(transaction.instanceDate)
        const year = date.getFullYear()
        const month = (date.getMonth() + 1).toString().padStart(2, 0)

        return {
            date: date,
            year: year,
            month: month,
            monthYear: `${month}-${year}`,
            balance: parseFloat(transaction.balance).toFixed(2),
            amount: parseFloat(transaction.amount).toFixed(2),
        }
    })

    const years = getUniqueArrayItems(
        transactionDatesAndBalances.map((transaction) => transaction.year)
    )

    const months = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"]

    const getMinMaxByMonth = (year, month) => {
        const currentMonthTransactions = transactionDatesAndBalances.filter(
            (transaction) =>
                transaction.year === year && transaction.month === month
        )

        if (currentMonthTransactions.length === 0) return false

        const min = currentMonthTransactions.reduce((a, b) =>
            parseFloat(a.balance) < parseFloat(b.balance) ? a : b
        )

        const max = currentMonthTransactions.reduce((a, b) =>
            parseFloat(a.balance) > parseFloat(b.balance) ? a : b
        )

        const date = dateHandler.toZeroHoursFormat(`${year}-${month}-01`)
        const monthName = date.toLocaleString('default', { month: 'long' })
        const displayMonth = `${year} — ${monthName}`

        return {
            date: date,
            year: year,
            displayMonth: displayMonth,
            minBalance: min.balance,
            maxBalance: max.balance,
        }
    }

    const minMaxByMonth = years.map((year) => {
        const minMaxByMonth = months.map((month) => {
            return getMinMaxByMonth(year, month)
        }).filter((month) => month !== false)

        return minMaxByMonth
    }).flat()

    return minMaxByMonth
}

export const getNetIncomeData = (transactionsList) => {
    const transactions = deepClone(transactionsList)

    const monthMap = new Map(
        transactions.map((transaction) => {
            const year = transaction.instanceDate.getFullYear()
            const month = transaction.instanceDate.getMonth()

            return [
                `${year}-${month}`,
                {
                    income: 0,
                    expenses: 0,
                    net: 0,
                    date: transaction.instanceDate,
                },
            ]
        })
    )

    transactions.forEach((transaction) => {
        const year = transaction.instanceDate.getFullYear()
        const month = transaction.instanceDate.getMonth()
        const key = `${year}-${month}`

        if (transaction.amount < 0) {
            monthMap.set(key, {
                ...monthMap.get(key),
                expenses:
                    monthMap.get(key).expenses + parseFloat(transaction.amount),
            })
        } else if (transaction.amount > 0) {
            monthMap.set(key, {
                ...monthMap.get(key),
                income:
                    monthMap.get(key).income + parseFloat(transaction.amount),
            })
        }

        monthMap.set(key, {
            ...monthMap.get(key),
            net: monthMap.get(key).net + parseFloat(transaction.amount),
        })
    })

    const dataAsArray = Array.from(monthMap).map(([key, value]) => {
        const splitKey = key.split('-')

        const month = new Date(splitKey[0], splitKey[1])
        const monthName = month.toLocaleString('default', { month: 'long' })
        const year = month.getFullYear()
        const displayMonth = `${year} — ${monthName}`

        return {
            date: value.date,
            year: year,
            month: monthName,
            displayMonth: displayMonth,
            income: parseFloat(value.income).toFixed(2),
            expenses: parseFloat(value.expenses).toFixed(2),
            net: parseFloat(value.net).toFixed(2),
        }
    })

    return dataAsArray
}

export const getExportData = (transactionsList) => {
    return transactionsList.map((transaction) => {
        return {
            amount: transaction.amount,
            balance: transaction.balance,
            category: transaction.category,
            created_date: transaction.created_date,
            day_of_month_list: transaction.day_of_month_list,
            day_of_week: transaction.day_of_week,
            description: transaction.description,
            end_date: transaction.end_date,
            hasExceptions: transaction.hasExceptions,
            id: transaction.id,
            instanceDate: transaction.instanceDate,
            is_recurring: transaction.is_recurring,
            month_of_year: transaction.month_of_year,
            recurring_type: transaction.recurring_type,
            separation_count: transaction.separation_count,
            start_date: transaction.start_date,
            subcategory: transaction.subcategory,
            title: transaction.title,
            week_of_month: transaction.week_of_month,
        }
    })
}

export const redirectAfterTimeout = (url, duration, navigate) => {
    setTimeout(() => {
        navigate(url)
    }, duration)
}

// Transactions



export const createFirstInstanceOfEachTransaction = (
    transactions,
    exceptions
) => {
    return transactions.map((transaction) => {
        const hasExceptions = checkTransactionForExceptionsById(
            transaction,
            exceptions
        )

        const firstInstanceDate = getFirstInstanceDate(transaction);
        
        let firstInstance = Object.assign({}, transaction, {
            instanceDate: firstInstanceDate,
            balance: 0,
            hasExceptions: hasExceptions,
        })

        return firstInstance
    })
}

export const createRecurrences = (
    transactions,
    exceptions,
    rangeBegin,
    rangeEnd,
    availableBalance,
    exceptionsOnly = false
) => {

    const firstInstanceOfEachTransaction = createFirstInstanceOfEachTransaction(
        transactions,
        exceptions
    )

    let recurrences = firstInstanceOfEachTransaction
        .filter(transaction => {
            if (exceptionsOnly) {
                return transaction.hasExceptions
            }
            return true
        })
        .map(transaction => {
            if (!transaction.is_recurring) {
                if (transaction.hasExceptions) {
                    const transactionWithExceptionInfo = addExceptionInfoToInstances([transaction], exceptions)[0]
                    
                    if (!instanceIsInDateRange(transactionWithExceptionInfo, rangeBegin, rangeEnd)) {
                        return null
                    }

                    return transactionWithExceptionInfo
                }

                if (!instanceIsInDateRange(transaction, rangeBegin, rangeEnd)) {
                    return null
                }
                
                return [transaction]
            }

            const transactionInstances = [transaction]
            const transactionEndDate = transaction.end_date
                ? dateHandler.toZeroHoursFormat(transaction.end_date)
                : rangeEnd

            let instanceDate = dateHandler.toZeroHoursFormat(transaction.instanceDate)

            let instanceCount = 0;

            // Create instances
            while (
                instanceDate.getTime() <= rangeEnd.getTime() &&
                instanceDate.getTime() <= transactionEndDate.getTime()
            ) {
                const currentInstanceDate = deepClone(instanceDate);

                const instance = Object.assign({}, transaction, {
                    instanceDate: currentInstanceDate,
                    balance: 0,
                })

                if (instanceCount === 0) {
                    instance.isFirst = true
                    transactionInstances[0] = instance
                }

                if (instanceCount > 0) {
                    transactionInstances.push(instance)
                }
                
                instanceDate = getNextInstanceDate(instanceDate, transaction)
                instanceCount++;
            }

            // Add isLast property to last instance
            const lastInstance = transactionInstances[
                transactionInstances.length - 1
            ]

            if (lastInstance.end_date !== null && transactionEndDate.getTime() <= rangeEnd.getTime()) {
                lastInstance.isLast = true
            }

            let instancesInRange;

            // If no exceptions, return filtered instances in range
            if (!transaction.hasExceptions) {
                instancesInRange = transactionInstances.filter(instance =>
                    instanceIsInDateRange(instance, rangeBegin, rangeEnd)
                )

                return instancesInRange.length ? instancesInRange : null
            } 

            // Add exception info & filter out instances not in range
            instancesInRange = addExceptionInfoToInstances(transactionInstances, exceptions)
                .filter(instance =>
                    instanceIsInDateRange(instance, rangeBegin, rangeEnd)
                )

            // If no instances in range, return null
            if (instancesInRange.length === 0) {
                return null
            }

            return instancesInRange
        })
        .flat()
        .filter((instance) => instance !== null)
        .sort((a, b) => a.instanceDate.getTime() - b.instanceDate.getTime())

    if (exceptionsOnly) {
        recurrences = recurrences.filter(instance => {
            return instance.hasOwnProperty("exceptionItems") && instance.exceptionItems.length > 0
        })

        return recurrences
    }

    addBalanceToInstances(recurrences, availableBalance)

    return recurrences
}

export const getFirstInstanceDate = (transaction) => {
    const {
        recurring_type: recurringType,
        start_date: startDate,
        day_of_month_list: dayOfMonthList,
        week_of_month: weekOfMonth,
        day_of_week: dayOfWeek,
        month_of_year: monthOfYear,
    } = transaction

    const currentDate = dateHandler.toZeroHoursFormat(startDate);

    switch (recurringType) {
        case 'daily': {
            return currentDate
        }

        case 'weekly': {
            const daysUntilNextDay = (dayOfWeek - currentDate.getDay() + 7) % 7
            currentDate.setDate(currentDate.getDate() + daysUntilNextDay)
            return currentDate
        }

        case 'monthly': {
            if (weekOfMonth) {
                return getNthDateOfMonth(recurringType, currentDate, weekOfMonth, dayOfWeek, null, true)
            }

            const dayOfMonthListClone = dayOfMonthList.split(',').map(Number).sort(
                (a, b) => a - b
            )

            const nextDayOfMonth = getNextDayOfMonth(currentDate, dayOfMonthListClone, 0)

            return nextDayOfMonth
        }

        case 'yearly': {
            if (weekOfMonth) {
                return getNthDateOfMonth(recurringType, currentDate, weekOfMonth, dayOfWeek, monthOfYear, true)
            }

            const dayOfMonthListClone = dayOfMonthList.split(',').map(Number).sort(
                (a, b) => a - b
            )

            currentDate.setMonth(monthOfYear)
            currentDate.setDate(dayOfMonthListClone[0])

            const nextDayOfMonth = getNextDayOfMonth(currentDate, dayOfMonthListClone, 0)
            return nextDayOfMonth
        }

        default:
            return currentDate
    }
}

export const getNextInstanceDate = (date, transaction) => {
    let currentDate = new Date(date)

    const {
        recurring_type: recurringType,
        separation_count: separationCount = 1,
        day_of_month_list: dayOfMonthList,
        week_of_month: weekOfMonth,
        day_of_week: dayOfWeek,
        month_of_year: monthOfYear,
    } = transaction

    switch (recurringType) {
        case 'daily': {
            currentDate.setDate(currentDate.getDate() + separationCount)
            break
        }

        case 'weekly': {
            if (dayOfWeek !== currentDate.getDay()) {
                const daysUntilNextDay = (dayOfWeek - currentDate.getDay() + 7) % 7
                currentDate.setDate(currentDate.getDate() + daysUntilNextDay)
            }

            currentDate.setDate(currentDate.getDate() + separationCount * 7)
            
            break
        }

        case 'monthly': {
            let dayOfMonthListClone = dayOfMonthList ? dayOfMonthList.split(',').map(Number).sort(
                (a, b) => a - b
            ) : null;

            if (dayOfMonthListClone && dayOfMonthListClone.length > 0) {
                const currentMonth = currentDate.getMonth();
                const currentDay = currentDate.getDate();
                const nextDayInList = dayOfMonthListClone.find(day => parseInt(day) > currentDay)

                if (!nextDayInList) {
                    currentDate.setMonth(currentMonth + separationCount)
                    currentDate.setDate(dayOfMonthListClone[0])
                }

                const nextDay = dayOfMonthListClone.find(day => day > currentDay)

                if (nextDay) {
                    currentDate.setDate(nextDay)
                    return currentDate
                }

                return currentDate
            }

            if (weekOfMonth) {
                const nthDate = getNthDateOfMonth(recurringType, currentDate, weekOfMonth, dayOfWeek)
                return nthDate
            }

            const nextDayOfMonth = getNextDayOfMonth(currentDate, dayOfMonthListClone, separationCount);
            return nextDayOfMonth
        }

        case 'yearly': {
            let dayOfMonthListClone = dayOfMonthList ? dayOfMonthList.split(',').map(Number).sort(
                (a, b) => a - b
            ) : null;

            if (dayOfMonthListClone && dayOfMonthListClone.length > 0) {
                const currentDay = currentDate.getDate();
                const nextDayInList = dayOfMonthListClone.find(day => parseInt(day) > currentDay)

                if (!nextDayInList) {
                    currentDate.setFullYear(currentDate.getFullYear() + separationCount)
                    currentDate.setMonth(monthOfYear)
                    currentDate.setDate(dayOfMonthListClone[0])
                }

                const nextDay = dayOfMonthListClone.find(day => day > currentDay)

                if (nextDay) {
                    currentDate.setDate(nextDay)
                    return currentDate
                }

                return currentDate
            }

            if (weekOfMonth) {
                currentDate.setMonth(currentDate.getMonth() + separationCount)
                const nthDate = getNthDateOfMonth(recurringType, currentDate, weekOfMonth, dayOfWeek, monthOfYear)
                return nthDate
            }

            const nextDayOfMonth = getNextDayOfMonth(currentDate, dayOfMonthListClone, separationCount);
            return nextDayOfMonth
        }

        default:
            return date
    }

    return currentDate
}

// Find the next valid day in a given month
const getNextDayOfMonth = (currentDate, days, separationCount) => {
    let nextDate = new Date(currentDate)
    nextDate.setMonth(nextDate.getMonth() + separationCount) // Move to next period

    // Find the next available day in the sorted list
    for (let day of days) {
        if (day >= nextDate.getDate()) {
            nextDate.setDate(day)
            return nextDate
        }
    }
    // If no valid day found, default to the first day in the list for the next period
    nextDate.setDate(days[0])
    return nextDate
}

function getNthDateOfMonth(recurringType, startDate, weekOfMonth, dayOfWeek, monthOfYear, firstInstance = false) {
    let year = startDate.getFullYear();
    let month = recurringType === 'monthly'
        ? startDate.getMonth() + (firstInstance ? 1 : 2)
        : monthOfYear + 1;
    let targetDate = null;

    if (recurringType === 'monthly' && month > 12) {
        year++;
        month = 1;
    }

    if (recurringType === 'yearly' && !firstInstance) {
        year++;
    }

    if (dayOfWeek === 7) { 
        // 'Day' (Specific day of the month)
        if (weekOfMonth === 'last') {
            let lastDay = new Date(year, month, 0);
            targetDate = new Date(year, month - 1, lastDay.getDate());
        } else {
            targetDate = new Date(year, month - 1, parseInt(weekOfMonth)); 
        }
    } else if (dayOfWeek === 8) { 
        // 'Weekday' (Monday - Friday)
        if (weekOfMonth === 'last') {
            let lastDay = new Date(year, month, 0);
            let lastWeekday = lastDay.getDay();
            let diff = (lastWeekday - 5 + 7) % 7;
            targetDate = new Date(year, month - 1, lastDay.getDate() - diff);
        } else {
            targetDate = getNthWeekday(year, month, parseInt(weekOfMonth));
        }
    } else if (dayOfWeek === 9) { 
        // 'Weekend Day' (Saturday or Sunday)
        if (weekOfMonth === 'last') {
            let lastDay = new Date(year, month, 0);
            let lastWeekday = lastDay.getDay();

            if (lastWeekday === 6 || lastWeekday === 0) {
                targetDate = new Date(year, month - 1, lastDay.getDate());
            } else {
                let diff = (lastWeekday) % 7;
                targetDate = new Date(year, month - 1, lastDay.getDate() - diff);
            }
        } else {
            targetDate = getNthWeekendDay(year, month, parseInt(weekOfMonth));
        }
    } else {
        // Specific Day of the Week (Sunday-Saturday)
        targetDate = getNthDayOfWeek(year, month, weekOfMonth, dayOfWeek);
    }

    return targetDate;
}

// Helper to find the nth occurrence of a specific day (0=Sunday, ..., 6=Saturday)
function getNthDayOfWeek(year, month, weekOfMonth, dayOfWeek) {
    if (weekOfMonth === 'last') {
        let lastDay = new Date(year, month, 0);
        let lastWeekday = lastDay.getDay();
        let diff = (lastWeekday - dayOfWeek + 7) % 7;
        return new Date(year, month - 1, lastDay.getDate() - diff);
    }

    let firstDay = new Date(year, month - 1, 1);
    let firstWeekday = firstDay.getDay();
    let diff = (dayOfWeek - firstWeekday + 7) % 7;
    let firstOccurrence = new Date(year, month - 1, 1 + diff);
    let targetDay = firstOccurrence.getDate() + (parseInt(weekOfMonth) - 1) * 7;
    let targetDate = new Date(year, month - 1, targetDay);
    return targetDate.getMonth() === firstDay.getMonth() ? targetDate : null;
}

// Helper to find the nth weekday (Mon-Fri)
function getNthWeekday(year, month, weekOfMonth) {
    let count = 0;
    for (let day = 1; day <= 31; day++) {
        let date = new Date(year, month - 1, day);
        if (date.getMonth() !== month - 1) break; // Stop if out of the month
        if (date.getDay() >= 1 && date.getDay() <= 5) { // Monday to Friday
            count++;
            if (count === weekOfMonth) return date;
        }
    }
    return null;
}

// Helper to find the nth weekend day (Saturday/Sunday)
function getNthWeekendDay(year, month, weekOfMonth) {
    let count = 0;
    for (let day = 1; day <= 31; day++) {
        let date = new Date(year, month - 1, day);
        if (date.getMonth() !== month - 1) break; // Stop if out of the month
        if (date.getDay() === 0 || date.getDay() === 6) { // Saturday or Sunday
            count++;
            if (count === weekOfMonth) return date;
        }
    }
    return null;
}



export const instanceIsInDateRange = (instance, begin, end) => {
    const transactionStartDate = dateHandler.toZeroHoursFormat(instance.start_date)
    const transactionStartDateTime = transactionStartDate.getTime()
    const instanceDateTime = instance.instanceDate.getTime()
    const isAfterBegin = instanceDateTime >= begin.getTime()
    const isBeforeEnd = instanceDateTime <= end.getTime()

    if (instance.end_date) {
        const instanceEndDateTime = dateHandler.toZeroHoursFormat(instance.end_date).getTime()
        let isBeforeTransactionEndDate = instanceDateTime <= instanceEndDateTime

        if (instance.daysAdjusted) {
            isBeforeTransactionEndDate = true;
        }

        return isAfterBegin && isBeforeEnd && isBeforeTransactionEndDate
    }

    if (instanceDateTime < transactionStartDateTime) {
        return false
    }

    return isAfterBegin && isBeforeEnd
}

export const checkTransactionForExceptionsById = (transaction, exceptions) => {
    return (
        exceptions.filter(
            (exception) => exception.transaction_id === transaction.id
        ).length > 0
    )
}

// Returns a copy of the instances array with exception info added
export const addExceptionInfoToInstances = (instances, allExceptions) => {
    const instancesClone = deepClone(instances)
    const potentialExceptionMatches = allExceptions.filter(exception =>
        exception.transaction_id === instancesClone[0].id
    )

    const instancesWithExceptionInfo = instancesClone.map((instance) => {
        const instanceDate = instance.instanceDate
        const matchingExceptions = potentialExceptionMatches.filter(exception =>
            instanceDate.getTime() >= dateHandler.toZeroHoursFormat(exception.start_date).getTime() &&
            instanceDate.getTime() <= dateHandler.toZeroHoursFormat(exception.end_date).getTime()
        )

        if (matchingExceptions.length) {
            matchingExceptions.forEach(exception => {
                instance = generateExceptionInfo(instance, instanceDate, exception)
            })
        }

        return instance
    })

    return instancesWithExceptionInfo
}

// Mutates the instances array by adding a balance property to each instance
export const addBalanceToInstances = (transactions, accumulator) =>
    transactions.reduce((prevValue, transaction, index, array) => {
        const sum = prevValue + parseFloat(transaction.amount)
        array[index].balance = sum
        return sum
    }, accumulator)


// Returns the updated instance object with exception info added
export const generateExceptionInfo = (instance, instanceDate, exception) => {
    const updatedInstance = deepClone(instance)
    const originalAmount = instance.amount

    updatedInstance.exceptionItems = updatedInstance.exceptionItems || []
    const exceptionStartDate = dateHandler.toZeroHoursFormat(exception.start_date);
    const exceptionEndDate = exception.end_date ? dateHandler.toZeroHoursFormat(exception.end_date) : null;

    const isSeries =
        exceptionStartDate &&
        exceptionEndDate &&
        exceptionStartDate.getTime() !== exceptionEndDate.getTime()

    const isFirstInSeries =
        isSeries &&
        instanceDate.getTime() === exceptionStartDate.getTime()


    let exceptionData = {
        exceptionId: exception.id,
        isSeries: isSeries,
        isFirstInSeries: isFirstInSeries,
        endDate: exceptionEndDate,
        originalAmount: originalAmount,
        firstExceptionInstanceDate: exceptionStartDate,
        exceptionEffects: [],
    }

    // Rescheduled
    if (exception.is_rescheduled) {
        const exceptionInfo = generateExceptionInfoRescheduled(exception, instance)

        updatedInstance.instanceDate = exceptionInfo.data.date
        updatedInstance.daysAdjusted = exceptionInfo.data.daysAdjusted

        exceptionData.exceptionEffects.push(exceptionInfo)
    }

    // Adjusted Amount
    if (
        !isNaN(parseFloat(exception.amount)) &&
        exception.amount !== originalAmount &&
        !exception.is_paid &&
        !exception.is_cancelled
    ) {
        updatedInstance.amount = exception.amount

        const exceptionInfo = generateExceptionInfoAmount(exception)
        exceptionData.exceptionEffects.push(exceptionInfo)
    }

    // Paid
    if (exception.is_paid) {
        updatedInstance.amount = 0

        const exceptionInfo = generateExceptionInfoPaid()
        exceptionData.exceptionEffects.push(exceptionInfo)
    }

    // Cancelled
    if (exception.is_cancelled) {
        updatedInstance.amount = 0

        const exceptionInfo = generateExceptionInfoCancelled()
        exceptionData.exceptionEffects.push(exceptionInfo)
    }

    if (exception.highlight) {
        updatedInstance.highlight = true

        const exceptionInfo = {
            type: 'highlight',
            data: {
                highlight: true,
            },
        }
        exceptionData.exceptionEffects.push(exceptionInfo)
    }

    updatedInstance.exceptionItems.push(exceptionData)

    return updatedInstance
}

export const generateExceptionInfoRescheduled = (exception, instance) => {
    const date = instance.instanceDate
    const numDaysAdjusted = exception.days_adjusted

    date.setDate(date.getDate() + parseInt(numDaysAdjusted))

    return {
        type: 'rescheduled',
        data: {
            date: date,
            daysAdjusted: numDaysAdjusted,
        },
    }
}

export const generateExceptionInfoAmount = (exception) => {
    return {
        type: 'amount',
        data: {
            amount: exception.amount,
        },
    }
}

export const generateExceptionInfoPaid = () => {
    return {
        type: 'paid',
        data: {
            amount: 0,
        },
    }
}

export const generateExceptionInfoCancelled = () => {
    return {
        type: 'cancelled',
        data: {
            amount: 0,
        },
    }
}

export const logger = (value, title) => {
    console.log('')
    console.log(`-------- ${title ?? ''} --------`)
    console.log(value)
}